import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { HeatingCircuit, Pza } from 'z3k-config';
import { useBox } from 'shared/hooks/utils';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { PZAPane } from '../../../utils/pza-pane';
import { hasOutputTemperatureSource } from 'z3k-parser/validation/z3k/config';
import { unpack_validation } from 'z3k-parser/validation/types';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { TemperatureSettingType } from './temperature-setting-type';
import { SettingFullRow } from '../../../material-utils/settings-card/full-row';
import { findLinksToPzaAndRemove } from 'z3k-parser/lib/pza';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
function PzaFieldsImpl(props) {
    const { mode, zone_index, circuit, temp_setting_type, validation_result, validation_warnings_result, } = props;
    const hmBox = useBox(mode);
    const hzIndexBox = useBox(zone_index);
    const { deviceFirmwareVersion, service_view } = useContext(Z3KSettings);
    const { config, replace_config: onChangeConfig } = useContext(Z3KSettingsEditor);
    const configBox = useBox(config);
    const pzas = useMemo(() => Pza.get.all(config), [config]);
    const has_outside_temperature_source = useMemo(() => hasOutputTemperatureSource(config, true), [config]);
    const onChangeHz = useCallback((config, param_key, changes) => HeatingMode.get.applySetterById(config, hmBox.current.id, {
        heating_zones: hmBox.current.heating_zones.map((hz, index) => hzIndexBox.current === index ? { ...hz, [param_key]: changes } : hz),
    }), []);
    const onAddPZA = useCallback((new_pza) => {
        const [newPzaId, newConfig] = Pza.get.add(configBox.current, new_pza);
        onChangeConfig(onChangeHz(newConfig, 'pza', newPzaId));
    }, [onChangeConfig, onChangeHz]);
    const onChangePZA = useCallback((new_pza) => {
        if (new_pza == null) {
            return;
        }
        onChangeConfig(Pza.get.replaceById(configBox.current, new_pza.id, new_pza));
    }, [onChangeConfig]);
    const onChangePZASelection = useCallback((newPzaId) => onChangeConfig(onChangeHz(configBox.current, 'pza', newPzaId)), [onChangeHz, onChangeConfig]);
    const onRemovePZA = useCallback((pza_to_del) => onChangeConfig(findLinksToPzaAndRemove(onChangeHz(configBox.current, 'pza', null), pza_to_del.id)), [onChangeConfig, onChangeHz]);
    const pzaObject = useMemo(() => {
        if (circuit != null && !HeatingCircuit.hasPZA(circuit)) {
            return null;
        }
        return pzas.find(({ id }) => id === mode.heating_zones[zone_index].pza) ?? null;
    }, [pzas, circuit, mode, zone_index]);
    const _validation_result = useMemo(() => unpack_validation(validation_result, 'pza'), [validation_result]);
    const _validation_warnings_result = useMemo(() => unpack_validation(validation_warnings_result, 'pza'), [validation_warnings_result]);
    return deviceFirmwareVersion > 63 &&
        service_view &&
        (HeatingCircuit.isBoiler(circuit) || HeatingCircuit.isConsumer(circuit)) &&
        HeatingCircuit.hasPZA(circuit) &&
        temp_setting_type !== TemperatureSettingType.off ? (_jsx(ErrorBoundary, { children: _jsx(SettingFullRow, { children: _jsx(PZAPane, { value: pzaObject, onChange: onChangePZA, onAdd: onAddPZA, onRemove: onRemovePZA, onChangeSelection: onChangePZASelection, pzas: pzas, has_outside_temperature_source: has_outside_temperature_source, validation_result: _validation_result, validation_warnings_result: _validation_warnings_result, notChosenTitle: "\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u044C \u041F\u0417\u0410, \u043D\u0430\u0437\u043D\u0430\u0447\u0435\u043D\u043D\u0443\u044E \u0432 \u043A\u043E\u043D\u0442\u0443\u0440\u0435" }) }) })) : null;
}
export const PzaFields = memo(PzaFieldsImpl);
